<template>
    <div class="_fd-language-config">
        <div class="_fc-l-label">{{ t('language.name') }}</div>
        <div class="_fc-l-info">
            {{ t('warning.language') }}
        </div>
        <div class="_fd-lc-header">
            <z-button size="small" @click="addColumn">{{ t('language.add') }}</z-button>
            <z-button size="small" type="danger" plain :disabled="!selected.length" @click="batchRmColumn">
                {{ t('language.batchRemove') }}
            </z-button>
        </div>
        <div class="_fd-lc-body">
            <table class="_fd-lc-table">
                <thead>
                    <tr>
                        <th class="_fd-lc-check">
                            <z-checkbox size="small" :model-value="allSelected" :indeterminate="partialSelected"
                                        @change="toggleAll"/>
                        </th>
                        <th class="_fd-lc-key">Key</th>
                        <th v-for="item in localeOptions" :key="item.value">{{ item.label }}</th>
                        <th class="_fd-lc-ops">{{ t('tableOptions.handle') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, ridx) in column" :key="row.key">
                        <td class="_fd-lc-check">
                            <z-checkbox size="small" :model-value="isSelected(row)"
                                        @change="(val) => toggleSelect(row, val)"/>
                        </td>
                        <td class="_fd-lc-key">{{ row.key }}</td>
                        <td v-for="item in localeOptions" :key="item.value">
                            <z-input v-if="row.input" size="small" v-model="row[item.value]"
                                     @blur="saveColumn(row, true)"></z-input>
                            <z-text v-else>{{ row[item.value] || '-' }}</z-text>
                        </td>
                        <td class="_fd-lc-ops">
                            <div class="_fd-lc-handle">
                                <i class="fc-icon icon-edit" v-if="!row.input" @click="row.input = true"></i>
                                <i class="fc-icon icon-check" v-else @click="saveColumn(row)"></i>
                                <i class="fc-icon icon-group" @click="copy(row.key)"></i>
                                <i class="fc-icon icon-delete-circle" @click="rmColumn(ridx)"></i>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

</template>

<script>
import {defineComponent} from 'vue';
import {copyTextToClipboard} from '../../utils';

export default defineComponent({
    name: 'LanguageConfig',
    inject: ['designer'],
    computed: {
        localeOptions() {
            return this.designer.setupState.getConfig('localeOptions', [
                {value: 'zh-cn', label: '简体中文'},
                {value: 'en', label: 'English'},
            ]);
        },
        t() {
            return this.designer.setupState.t;
        },
        allSelected() {
            return this.column.length > 0 && this.selected.length === this.column.length;
        },
        partialSelected() {
            return this.selected.length > 0 && this.selected.length < this.column.length;
        },
    },
    data() {
        return {
            column: [],
            uni: 0,
            selected: [],
        }
    },
    methods: {
        copy(key) {
            copyTextToClipboard(key);
        },
        addColumn() {
            this.column.unshift({
                key: this.randomString(),
                input: true,
            })
        },
        saveColumn(row, input) {
            row.input = input || false;
            const language = this.designer.setupState.formOptions.language;
            this.localeOptions.forEach(item => {
                if (!language[item.value]) {
                    language[item.value] = {};
                }
                language[item.value][row.key] = row[item.value];
            })
        },
        rmColumn(idx) {
            const row = this.column[idx];
            this.column.splice(idx, 1);
            this.selected = this.selected.filter(item => item.key !== row.key);
            const language = this.designer.setupState.formOptions.language;
            this.localeOptions.forEach(item => {
                if (language[item.value]) {
                    delete language[item.value][row.key]
                }
            })
        },
        batchRmColumn() {
            this.selected.forEach(item => {
                this.rmColumn(this.column.indexOf(item));
            });
            this.selected = [];
        },
        isSelected(row) {
            return this.selected.some(item => item.key === row.key);
        },
        toggleSelect(row, checked) {
            if (checked) {
                if (!this.isSelected(row)) {
                    this.selected.push(row);
                }
            } else {
                this.selected = this.selected.filter(item => item.key !== row.key);
            }
        },
        toggleAll(checked) {
            this.selected = checked ? [...this.column] : [];
        },
        randomString() {
            const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            let result = '';
            const charactersLength = characters.length;

            for (let i = 0; i < 7; i++) {
                result += characters.charAt(Math.floor(Math.random() * charactersLength));
            }
            return characters.charAt((this.uni++) % 26) + result;
        }
    },
    mounted() {
        const language = this.designer.setupState.formOptions.language || {};
        const column = {};
        Object.keys(language).forEach(lang => {
            Object.keys(language[lang]).forEach(key => {
                if (!column[key]) {
                    column[key] = {
                        key: key,
                    }
                }
                column[key][lang] = language[lang][key];
            })
        });
        this.column = Object.values(column);
    }

});
</script>

<style>
._fd-language-config {
    height: 100%;
    overflow: auto;
}

._fd-lc-body, ._fd-lc-header {
    padding: 0 12px;
}

._fd-lc-body {
    overflow: auto;
}

._fd-lc-header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
}

._fd-lc-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
}

._fd-lc-table th,
._fd-lc-table td {
    height: 34px;
    border: 1px solid var(--z-border-color-lighter, #ebeef5);
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 400;
    text-align: left;
    vertical-align: middle;
}

._fd-lc-table th {
    background: var(--z-fill-color-light, #f5f7fa);
    color: var(--z-text-color-regular, #606266);
}

._fd-lc-check {
    width: 36px;
    text-align: center;
}

._fd-lc-key {
    width: 90px;
}

._fd-lc-ops {
    width: 88px;
}

._fd-language-config ._fc-l-info {
    margin-bottom: 12px;
}

._fd-lc-handle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
}
</style>
